import { atom, Setter } from 'jotai';
import { atomFamily } from 'jotai/utils';

import searchAtomPair from 'atoms/search-atom';
import { isActivityFeedPanelOpenAtom } from 'components/activity-feed-panel';
import type { DataTableSelectionState } from 'components/data-table';
import {
  atomWithLoadable,
  createResourceAtom,
  mutationAtomWithLoadable,
} from 'lib/atoms';
import request from 'lib/request';
import type { InterestWorkspaceResponse } from 'types/api-responses';

import type {
  BulkFailureEntry,
  ConfirmationSource,
  ConfirmPanelState,
  HoldCommentResponse,
  SlotDetailResponse,
  SlotDetailTarget,
  SlotInterestRow,
  SlotsParams,
  StreamCode,
  WorkspaceSlotsAggregations,
} from './types';
import { failedWriteMessage, type WriteFailure } from './utils';

/**
 * Fetches a single interest-certification workspace by id.
 *
 * Uses `keep-old-data-on-refetch` so a write to the atom refetches while the
 * previously-loaded record stays visible — consumers don't unmount during the
 * refresh.
 *
 * @param id - Workspace id from the route param.
 * @return A writable resource atom (see `createResourceAtom`).
 */
export const workspaceAtomFamily = atomFamily((id: number) =>
  createResourceAtom<InterestWorkspaceResponse>(
    `/interest_certification/workspaces/${id}`,
    'keep-old-data-on-refetch'
  )
);

/** Logical consumer of the per-surface slot-list search atom. */
export type SlotListSurface = 'comparison' | 'promote' | 'tasks';

/** atomFamily key for the per-surface slot-list search atom. */
interface SlotListKey {
  surface: SlotListSurface;
  workspaceId: number;
}

/**
 * Per-surface, per-workspace search atom for the slot list endpoint. One
 * `atomFamily` covers three independent surfaces — the Comparison Table, the
 * Promote modal, and the Tasks side panel — so each gets isolated
 * pagination / search / sort state, while a single factory body avoids the
 * copy-paste drift of three identical families.
 *
 * The factory body ignores the key (`href` is constant — workspace id travels
 * in the payload). atomFamily caches one atom per `{surface, workspaceId}`
 * pair via the equality function below.
 *
 * @return The slot-list `searchAtom` for the given surface + workspace.
 */
export const slotListAtomFamily = atomFamily(
  (_key: SlotListKey) =>
    searchAtomPair<
      SlotInterestRow,
      SlotsParams,
      undefined,
      WorkspaceSlotsAggregations
    >({ href: '/queries/interest_certification_workspace_slots' }).searchAtom,
  (a, b) => a.surface === b.surface && a.workspaceId === b.workspaceId
);

/** Request body for the workspace filter-values query. */
export interface WorkspaceFilterValuesRequest {
  query: { workspace_id: number };
}

/**
 * Distinct filter-dimension values across every slot in a workspace, returned
 * by `POST /queries/interest_certification_workspace_filter_values`. The
 * Operator multi-select consumes `operators` and the Well Status multi-select
 * consumes `wellStatuses`; `formations` and `wellNames` are returned by the
 * endpoint but not surfaced in any filter.
 */
export interface WorkspaceFilterValuesResponse {
  operators: string[];
  formations: string[];
  wellNames: string[];
  wellStatuses: string[];
}

/**
 * Per-workspace distinct filter values for the Operator multi-select.
 * Auto-fetches on first read and caches per workspace id; not re-fired, since
 * filter values are stable for a workspace session.
 *
 * @param workspaceId - Workspace whose filter values to fetch.
 * @return A loadable atom resolving to `WorkspaceFilterValuesResponse`.
 */
export const workspaceFilterValuesAtomFamily = atomFamily(
  (workspaceId: number) => {
    const [, loadableAtom] = atomWithLoadable<WorkspaceFilterValuesResponse>(
      async () => {
        const payload: WorkspaceFilterValuesRequest = {
          query: { workspace_id: workspaceId },
        };
        const response = await request.post<
          WorkspaceFilterValuesResponse,
          { error: string }
        >(
          '/queries/interest_certification_workspace_filter_values',
          payload,
          new AbortController()
        );
        if (response.type === 'success' && response.data) {
          return response.data;
        }
        const errorMessage =
          response.type === 'error' && response.data?.error
            ? response.data.error
            : 'Failed to load filter values';
        throw new Error(errorMessage);
      }
    );
    return loadableAtom;
  }
);

// ───────────────────────────────────────────────────────────────────────────
// Confirmation workflow — mutation atoms + panel state
//
// Co-located with the fetch atoms above so the post-success invalidator can
// re-fire `slotListAtomFamily` / `workspaceAtomFamily` directly (no import),
// and so all workspace-shell atoms live in one file. API request/response
// types are inlined next to their atoms, matching the filter-values pattern.
// ───────────────────────────────────────────────────────────────────────────

/** Single-slot confirm body — always the per-tract `tracts: []` shape. */
export type ConfirmSlotRequest = ConfirmTractsRequest;

/**
 * One per-tract confirmation entry (partial confirmations). AM entries omit
 * values (the BE certifies the live per-(subtract, slot) AM); DO/Custom carry
 * the tract's certified values. `subtract_id: null` is the no-subtract
 * pseudo-tract pathway.
 */
export interface ConfirmTractEntry {
  subtract_id: string | null;
  /** Per-UAT grain (ONCALL-668): the segment's UAT id, so the confirm targets
   *  the right `(workspace_slot, subtract, UAT)` row. Null for the no-subtract /
   *  null-UAT (slot-level) pathway. */
  unit_allocation_tract_id: string | null;
  source: ConfirmationSource;
  note: string | null;
  streams: StreamCode[];
  confirmed_nri?: number;
  confirmed_wi?: number;
}

/**
 * Per-tract confirm body: each entry confirms independently (own source /
 * note / streams / values); the whole array is atomic on the BE. "Confirm All
 * Remaining" is the same request covering every uncertified tract.
 */
export interface ConfirmTractsRequest {
  confirmation: {
    tracts: ConfirmTractEntry[];
  };
}

/** The confirm endpoint returns the updated, consolidated slot row. */
/**
 * Single-slot confirm endpoint returns the slot row WITHOUT the per-subtract
 * enrichment block — the confirm view renders `_row.json.jbuilder` without
 * passing `include_subtracts: true`, so the BE omits `subtracts:` from the
 * wire. `Omit` keeps the FE type honest so consumers can't accidentally read
 * a phantom field.
 */
export type ConfirmSlotResponse = Omit<SlotInterestRow, 'subtracts'>;

/**
 * Bulk confirm body — Allocation Manager only, explicit-slots mode. One entry
 * per selected consolidated row, carrying the streams that row represented so
 * the BE confirms every selected stream (not just the representative row). The
 * BE resolves each entry by slot_id + stream.
 */
export interface BulkConfirmRequest {
  source: 'allocation_manager';
  /** Per-UAT grain (ONCALL-668): an entry MAY carry `unit_allocation_tract_id`
   *  to narrow the row's bulk AM confirm to one UAT; omitted (or null) confirms
   *  the whole slot, the common bulk pathway. The BE keys one UAT per slot_id. */
  slots: {
    slot_id: string;
    streams: StreamCode[];
    unit_allocation_tract_id?: string | null;
  }[];
  /**
   * Optional supporting note applied to every bulk-certified slot. AM never
   * requires a note; omitted when the user leaves the field blank.
   */
  note?: string;
}

/** Bulk confirm result. `failed[]` is non-empty on a 207 partial success. */
export interface BulkConfirmResponse {
  confirmedCount: number;
  failed: BulkFailureEntry[];
}

/**
 * Bulk unconfirm body — clears the active confirmation on each selected row's
 * streams. One entry per consolidated row; the BE resolves each by slot_id +
 * stream and stamps one shared `unconfirmed_at`, so the activity feed collapses
 * the cleared streams into a single "Unconfirmed Interest" card. Reuses the
 * bulk-confirm `slots` element shape ({ slot_id, streams }) but carries NO
 * `source` (unlike `BulkConfirmRequest`).
 */
export interface BulkUnconfirmRequest {
  slots: BulkConfirmRequest['slots'];
}

/**
 * Bulk unconfirm result. `failed[]` is non-empty on a 207 partial success —
 * rare for uncertify (only true server errors); a 422 (no valid slots) rejects
 * the mutation instead.
 */
export interface BulkUnconfirmResponse {
  unconfirmedCount: number;
  failed: BulkFailureEntry[];
}

/**
 * Bulk hold body — puts each selected tract's streams On Hold under one shared
 * reason comment. One entry per (slot, tract); the BE resolves each by slot_id
 * + stream and stamps one shared `held_at`, so the activity feed collapses the
 * held streams into a single "Put on hold" card. Holds are per-tract, so each
 * entry carries `subtract_id` (unlike the bulk-confirm `slots` shape).
 */
export interface BulkHoldRequest {
  comment: string;
  /** Per-UAT grain (ONCALL-668): each entry MAY carry the segment's UAT so the
   *  hold targets the right `(workspace_slot, subtract, UAT)` row. */
  slots: {
    slot_id: string;
    subtract_id: string;
    streams: StreamCode[];
    unit_allocation_tract_id?: string | null;
  }[];
}

/** Bulk hold result. `failed[]` is non-empty on a 207 (e.g. already on hold). */
export interface BulkHoldResponse {
  heldCount: number;
  failed: BulkFailureEntry[];
}

/**
 * Bulk release body — clears the active holds on each selected tract's streams.
 * One entry per (slot, tract); the BE resolves each by slot_id + stream and
 * stamps one shared `released_at`, so the activity feed collapses the released
 * streams into a single "Removed On Hold" card.
 */
export interface BulkReleaseRequest {
  slots: BulkHoldRequest['slots'];
}

/** Bulk release result. `failed[]` is non-empty on a 207 partial success. */
export interface BulkReleaseResponse {
  releasedCount: number;
  failed: BulkFailureEntry[];
}

/** Auto-confirm body — the percentage threshold persisted on the workspace. */
export interface AutoConfirmRequest {
  threshold_pct: number;
}

/** Auto-confirm result — the recompute's per-bucket counts plus `failed[]`. */
export interface AutoConfirmResponse {
  confirmedCount: number;
  alreadyConfirmedCount: number;
  skippedCount: number;
  failed: BulkFailureEntry[];
}

/** null when the Confirm Interest panel is closed; carries the row(s) when open. */
export const confirmPanelStateAtom = atom<ConfirmPanelState>(null);

/**
 * Monotonic count of confirmation events (confirm / unconfirm / hold / release)
 * this session. Bumped by `invalidateAfterConfirmationEvent` at the moment a
 * mutation lands, BEFORE the refetches it triggers.
 *
 * INS-11586: the Certify panel compares this against the generation of the last
 * slot-list fetch that actually settled, so it can tell "this row reflects your
 * edit" from "this row predates it". The list is intentionally
 * stale-while-revalidate (rows must not blank on every mutation), but the panel
 * renders ONE row's editable state, where briefly-stale means showing reviews
 * the user just cleared — the bug this ticket exists for. The counter lets the
 * panel apply a stricter rule than the table without changing the fetch policy.
 */
export const confirmationGenerationAtom = atom(0);

/**
 * Re-fires the slot list + workspace atoms after any confirmation event
 * succeeds — the FR #6 refetch-on-success contract. Called from each mutation
 * writer so the cross-atom coupling is visible at the definition; no imperative
 * store access in component / hook code.
 *
 * @param {Setter} set - Jotai setter handed to the mutation writer.
 * @param {number} workspaceId - Workspace whose slot list + record to refresh.
 */
const invalidateAfterConfirmationEvent = (
  set: Setter,
  workspaceId: number
): void => {
  // Bump BEFORE the refetches: the panel treats "a fetch settled while the
  // generation was N" as proof the data includes mutation N, so the new
  // generation has to be visible by the time `loading` rises.
  set(confirmationGenerationAtom, (n) => n + 1);

  // searchAtomPair treats any write as "refetch with this payload"; re-passing
  // the current payload re-issues the same query (stale-while-revalidate).
  // No-op only before the first search establishes a payload — unreachable
  // while a confirmation panel (opened from a loaded row) is in play. Refire
  // every surface that holds slot rows so the Comparison Table, Promote modal,
  // and Tasks side panel all repaint together.
  set(
    slotListAtomFamily({ surface: 'comparison', workspaceId }),
    (prev) => prev
  );
  set(slotListAtomFamily({ surface: 'promote', workspaceId }), (prev) => prev);
  set(slotListAtomFamily({ surface: 'tasks', workspaceId }), (prev) => prev);
  // No-arg write triggers the createResourceAtom SWR refresh — cached data
  // stays visible while loading goes true.
  set(workspaceAtomFamily(workspaceId));
};

/**
 * Single-slot confirm. POSTs the chosen source + allocations, then invalidates
 * the slot list + workspace record so the table and Tasks counter repaint from
 * fresh BE data.
 */
export const [confirmSlotAtom, confirmSlotLoadableAtom] =
  mutationAtomWithLoadable<
    ConfirmSlotResponse,
    [workspaceId: number, workspaceSlotId: number, payload: ConfirmSlotRequest]
  >(async (_get, set, workspaceId, workspaceSlotId, payload) => {
    const url = `/interest_certification/workspaces/${workspaceId}/slots/${workspaceSlotId}/confirmations`;
    const response = await request.post<ConfirmSlotResponse, WriteFailure>(
      url,
      payload,
      new AbortController()
    );

    if (response.type !== 'success' || !response.data) {
      throw new Error(failedWriteMessage(response, 'Failed to confirm slot'));
    }

    invalidateAfterConfirmationEvent(set, workspaceId);
    return response.data;
  });

/**
 * Bulk confirm to Allocation Manager. Returns `{ confirmedCount, failed[] }`;
 * the panel consumes `failed[]` for the partial-failure UX, then the slot list
 * + workspace refetch on success.
 */
export const [bulkConfirmAtom, bulkConfirmLoadableAtom] =
  mutationAtomWithLoadable<
    BulkConfirmResponse,
    [workspaceId: number, slots: BulkConfirmRequest['slots'], note?: string]
  >(async (_get, set, workspaceId, slots, note) => {
    const url = `/interest_certification/workspaces/${workspaceId}/bulk_confirmations`;
    const body: BulkConfirmRequest = {
      source: 'allocation_manager',
      slots,
      // Optional — only sent when the user typed a supporting note.
      ...(note ? { note } : {}),
    };
    const response = await request.post<BulkConfirmResponse, { error: string }>(
      url,
      body,
      new AbortController()
    );

    if (response.type !== 'success' || !response.data) {
      const errorMessage =
        response.type === 'error' && response.data?.error
          ? response.data.error
          : 'Failed to confirm slots';
      throw new Error(errorMessage);
    }

    invalidateAfterConfirmationEvent(set, workspaceId);
    return response.data;
  });

/**
 * Bulk unconfirm. Mirrors `bulkConfirmAtom` — same `{ slot_id, streams }[]`
 * payload and post-success invalidation — with three differences: no `source`
 * in the body, the `bulk_unconfirmations` URL, and an `unconfirmedCount`
 * result. Drives "uncertify all" for a row: passing every certified stream in
 * one request makes the BE stamp one shared `unconfirmed_at`, so the activity
 * feed merges them into a single card (N single-stream DELETEs stay separate).
 */
export const [bulkUnconfirmAtom, bulkUnconfirmLoadableAtom] =
  mutationAtomWithLoadable<
    BulkUnconfirmResponse,
    [workspaceId: number, slots: BulkConfirmRequest['slots']]
  >(async (_get, set, workspaceId, slots) => {
    const url = `/interest_certification/workspaces/${workspaceId}/bulk_unconfirmations`;
    const body: BulkUnconfirmRequest = { slots };
    const response = await request.post<
      BulkUnconfirmResponse,
      { error: string }
    >(url, body, new AbortController());

    if (response.type !== 'success' || !response.data) {
      const errorMessage =
        response.type === 'error' && response.data?.error
          ? response.data.error
          : 'Failed to unconfirm slots';
      throw new Error(errorMessage);
    }

    invalidateAfterConfirmationEvent(set, workspaceId);
    return response.data;
  });

/**
 * Bulk hold. Puts every selected tract's streams On Hold in one request with a
 * shared reason comment; the BE stamps one `held_at` so the activity feed shows
 * a single "Put on hold" card. (The per-tract hold endpoint already multi-streams
 * one tract — this is the cross-tract analogue.)
 */
export const [bulkHoldAtom, bulkHoldLoadableAtom] = mutationAtomWithLoadable<
  BulkHoldResponse,
  [workspaceId: number, comment: string, slots: BulkHoldRequest['slots']]
>(async (_get, set, workspaceId, comment, slots) => {
  const url = `/interest_certification/workspaces/${workspaceId}/bulk_holds`;
  const body: BulkHoldRequest = { comment, slots };
  const response = await request.post<BulkHoldResponse, { error: string }>(
    url,
    body,
    new AbortController()
  );

  if (response.type !== 'success' || !response.data) {
    const errorMessage =
      response.type === 'error' && response.data?.error
        ? response.data.error
        : 'Failed to place holds';
    throw new Error(errorMessage);
  }

  invalidateAfterConfirmationEvent(set, workspaceId);
  return response.data;
});

/**
 * Bulk release. Clears the active holds on every selected tract's streams in one
 * request; the BE stamps one `released_at` so the activity feed shows a single
 * "Removed On Hold" card. Passing all of a tract's held streams releases them
 * together — the single-stream DELETE (`releaseTractAtom`) only cleared the
 * representative workspace_slot, leaving sibling streams held.
 */
export const [bulkReleaseAtom, bulkReleaseLoadableAtom] =
  mutationAtomWithLoadable<
    BulkReleaseResponse,
    [workspaceId: number, slots: BulkReleaseRequest['slots']]
  >(async (_get, set, workspaceId, slots) => {
    const url = `/interest_certification/workspaces/${workspaceId}/bulk_releases`;
    const body: BulkReleaseRequest = { slots };
    const response = await request.post<BulkReleaseResponse, { error: string }>(
      url,
      body,
      new AbortController()
    );

    if (response.type !== 'success' || !response.data) {
      const errorMessage =
        response.type === 'error' && response.data?.error
          ? response.data.error
          : 'Failed to clear review';
      throw new Error(errorMessage);
    }

    invalidateAfterConfirmationEvent(set, workspaceId);
    return response.data;
  });

/**
 * Auto-confirm threshold apply. Persists `threshold_pct` on the workspace and
 * recomputes auto-managed slots; the workspace refetch brings back the new
 * `autoConfirmThresholdPct`, which propagates to the toolbar label and every
 * DeltaIndicator cell.
 */
export const [autoConfirmAtom, autoConfirmLoadableAtom] =
  mutationAtomWithLoadable<
    AutoConfirmResponse,
    [workspaceId: number, thresholdPct: number]
  >(async (_get, set, workspaceId, thresholdPct) => {
    const url = `/interest_certification/workspaces/${workspaceId}/auto_confirm`;
    const body: AutoConfirmRequest = { threshold_pct: thresholdPct };
    const response = await request.post<AutoConfirmResponse, { error: string }>(
      url,
      body,
      new AbortController()
    );

    if (response.type !== 'success' || !response.data) {
      const errorMessage =
        response.type === 'error' && response.data?.error
          ? response.data.error
          : 'Failed to update auto-confirm threshold';
      throw new Error(errorMessage);
    }

    invalidateAfterConfirmationEvent(set, workspaceId);
    return response.data;
  });

/**
 * The promote endpoint renders the same `interest_certification/workspaces/show`
 * jbuilder as the workspace fetch, so the response carries every field on the
 * cached record — including the now-`true` `promoted` flag the cascade reads.
 */
export type PromoteWorkspaceResponse = InterestWorkspaceResponse;

/**
 * Promotes the workspace. POSTs to the action route with no body, then
 * refetches *only* the workspace record — promote flips `workspace.promoted`
 * but doesn't touch slot rows or aggregations, so the slot-list refetch the
 * confirmation invalidator triggers would be wasted work.
 */
export const [promoteWorkspaceAtom, promoteWorkspaceLoadableAtom] =
  mutationAtomWithLoadable<PromoteWorkspaceResponse, [workspaceId: number]>(
    async (_get, set, workspaceId) => {
      const url = `/interest_certification/workspaces/${workspaceId}/promotions`;
      const response = await request.post<
        PromoteWorkspaceResponse,
        WriteFailure
      >(url, {}, new AbortController());

      if (response.type !== 'success' || !response.data) {
        throw new Error(
          failedWriteMessage(response, 'Failed to promote workspace')
        );
      }

      set(workspaceAtomFamily(workspaceId));
      return response.data;
    }
  );

export const promoteModalOpenAtom = atom<boolean>(false);

/**
 * Renames the workspace (PATCH /interest_certification/workspaces/:id).
 * Refetches the workspace atom on success so the header and activity-feed
 * subtitle update without a full page reload.
 */
export const [renameWorkspaceAtom, renameWorkspaceLoadableAtom] =
  mutationAtomWithLoadable<
    InterestWorkspaceResponse,
    [workspaceId: number, name: string]
  >(async (_get, set, workspaceId, name) => {
    const response = await request.patch<
      InterestWorkspaceResponse,
      { error: string }
    >(
      `/interest_certification/workspaces/${workspaceId}`,
      { workspace: { name } },
      new AbortController()
    );

    if (response.type !== 'success' || !response.data) {
      const errorMessage =
        response.type === 'error' && response.data?.error
          ? response.data.error
          : 'Failed to rename workspace';
      throw new Error(errorMessage);
    }

    set(workspaceAtomFamily(workspaceId));
    return response.data;
  });

/**
 * Updates the workspace's free-text description (PATCH
 * /interest_certification/workspaces/:id). Mirrors the rename mutation: sends
 * only the description key and refetches the workspace atom on success so the
 * Summary card reflects the new value without a full reload. An empty string
 * clears the description (BE stores it as-is; the column is nullable).
 */
export const [
  updateWorkspaceDescriptionAtom,
  updateWorkspaceDescriptionLoadableAtom,
] = mutationAtomWithLoadable<
  InterestWorkspaceResponse,
  [workspaceId: number, description: string]
>(async (_get, set, workspaceId, description) => {
  const response = await request.patch<
    InterestWorkspaceResponse,
    { error: string }
  >(
    `/interest_certification/workspaces/${workspaceId}`,
    { workspace: { description } },
    new AbortController()
  );

  if (response.type !== 'success' || !response.data) {
    const errorMessage =
      response.type === 'error' && response.data?.error
        ? response.data.error
        : 'Failed to update description';
    throw new Error(errorMessage);
  }

  set(workspaceAtomFamily(workspaceId));
  return response.data;
});

/**
 * Clears the active SlotConfirmation on a slot. BE returns 204 No Content
 * (DELETE /interest_certification/workspaces/:id/slots/:slot_id/confirmations/active).
 * Idempotent BE-side: a repeat call after success returns 204 without producing
 * a duplicate audit event.
 *
 * Refetches the workspace + slot lists on success so the row's Status drops to
 * "Not Started", the Tasks counter ticks down, and the summary tiles recompute.
 */
export const [unconfirmSlotAtom, unconfirmSlotLoadableAtom] =
  mutationAtomWithLoadable<
    null,
    [workspaceId: number, workspaceSlotId: number]
  >(async (_get, set, workspaceId, workspaceSlotId) => {
    const url = `/interest_certification/workspaces/${workspaceId}/slots/${workspaceSlotId}/confirmations/active`;
    const response = await request.deleteReq<null, { error: string }>(
      url,
      undefined,
      new AbortController()
    );

    if (response.type !== 'success') {
      const errorMessage =
        response.type === 'error' && response.data?.error
          ? response.data.error
          : 'Failed to unconfirm slot';
      throw new Error(errorMessage);
    }

    invalidateAfterConfirmationEvent(set, workspaceId);
    return null;
  });

/** Row targeted by the Unconfirm modal; null when the modal is closed. */
export const unconfirmTargetRowAtom = atom<SlotInterestRow | null>(null);

/**
 * Open-state for the "switching to Allocation Manager overwrites Custom values"
 * warning (INS-9897). `null` when closed. `count` is the number of affected
 * tracts/rows shown in the message; `onConfirm` is the deferred submit the
 * panel resumes when the user confirms. Storing the callback in the atom keeps
 * the resume logic co-located with the trigger (the same atom-driven-open
 * pattern the Unconfirm modal uses), so the panel doesn't have to thread the
 * pending submit through component state.
 */
export interface CustomLossWarning {
  count: number;
  onConfirm: () => void;
}

/** Open-state for the Custom-loss warning modal; null when closed. */
export const customLossWarningAtom = atom<CustomLossWarning | null>(null);
// ───────────────────────────────────────────────────────────────────────────
// Per-tract On Hold lifecycle (INS-9896)
//
// Grain per (workspace_slot, subtract_id). `streams[]` is authoritative and
// fans out across the slot's per-stream rows, exactly like a confirmation —
// the BE resolves the selected streams' workspace_slot rows from the path
// slot. All three mutations re-fire the slot list + workspace atoms on success
// (shared `invalidateAfterConfirmationEvent`) so held badges, the slot-level
// rollup, and the Tasks counter repaint from fresh BE state.
// ───────────────────────────────────────────────────────────────────────────

/** Put-on-hold body. `comment` is the required reason (the thread's first
 *  entry); `streams[]` the user-selected streams to hold (required). */
export interface HoldTractRequest {
  hold: {
    subtract_id: string;
    /** Per-UAT grain (ONCALL-668): the segment's UAT id, so the hold targets the
     *  right `(workspace_slot, subtract, UAT)` row. Null for the null-UAT
     *  segment. */
    unit_allocation_tract_id: string | null;
    comment: string;
    streams: StreamCode[];
  };
}

/** Append-comment body — adds to the active hold's thread. */
export interface AddHoldCommentRequest {
  hold: {
    subtract_id: string;
    unit_allocation_tract_id: string | null;
    body: string;
  };
}

/**
 * Puts the selected streams of a tract On Hold. Returns the re-derived slot row
 * (same `_row` shape as the slot list, with `subtracts[]` + the hold overlay),
 * then invalidates so the table repaints.
 */
export const [holdTractAtom, holdTractLoadableAtom] = mutationAtomWithLoadable<
  SlotInterestRow,
  [workspaceId: number, workspaceSlotId: number, payload: HoldTractRequest]
>(async (_get, set, workspaceId, workspaceSlotId, payload) => {
  const url = `/interest_certification/workspaces/${workspaceId}/slots/${workspaceSlotId}/holds`;
  const response = await request.post<SlotInterestRow, WriteFailure>(
    url,
    payload,
    new AbortController()
  );

  if (response.type !== 'success' || !response.data) {
    throw new Error(
      failedWriteMessage(response, 'Failed to mark tract for review')
    );
  }

  invalidateAfterConfirmationEvent(set, workspaceId);
  return response.data;
});

/**
 * Releases the active hold for `(workspace_slot, subtract_id)`. `subtract_id`
 * rides as a query param because the BE `destroy` reads it from top-level
 * params (not the wrapped `hold` body). BE returns 204; idempotent.
 */
export const [releaseTractAtom, releaseTractLoadableAtom] =
  mutationAtomWithLoadable<
    null,
    [workspaceId: number, workspaceSlotId: number, subtractId: string]
  >(async (_get, set, workspaceId, workspaceSlotId, subtractId) => {
    const url = `/interest_certification/workspaces/${workspaceId}/slots/${workspaceSlotId}/holds/active?subtract_id=${encodeURIComponent(
      subtractId
    )}`;
    const response = await request.deleteReq<null, { error: string }>(
      url,
      undefined,
      new AbortController()
    );

    if (response.type !== 'success') {
      const errorMessage =
        response.type === 'error' && response.data?.error
          ? response.data.error
          : 'Failed to clear review';
      throw new Error(errorMessage);
    }

    invalidateAfterConfirmationEvent(set, workspaceId);
    return null;
  });

/**
 * Appends a comment to the active hold's thread. Returns the created comment
 * (sans `userName`), then invalidates so the refetched row carries the full
 * thread with resolved names.
 */
export const [addHoldCommentAtom, addHoldCommentLoadableAtom] =
  mutationAtomWithLoadable<
    HoldCommentResponse,
    [
      workspaceId: number,
      workspaceSlotId: number,
      payload: AddHoldCommentRequest,
    ]
  >(async (_get, set, workspaceId, workspaceSlotId, payload) => {
    const url = `/interest_certification/workspaces/${workspaceId}/slots/${workspaceSlotId}/holds/comments`;
    const response = await request.post<HoldCommentResponse, { error: string }>(
      url,
      payload,
      new AbortController()
    );

    if (response.type !== 'success' || !response.data) {
      const errorMessage =
        response.type === 'error' && response.data?.error
          ? response.data.error
          : 'Failed to add comment';
      throw new Error(errorMessage);
    }

    invalidateAfterConfirmationEvent(set, workspaceId);
    return response.data;
  });

// ───────────────────────────────────────────────────────────────────────────
// Slot Details side panel (E-12)
// ───────────────────────────────────────────────────────────────────────────

/**
 * Open-state for the Slot Details side panel. Holds the row identity the panel
 * needs to build its fetch URL + title; `null` when the panel is closed. The
 * "Show details" action in the comparison table writes it; the panel's close
 * handler clears it.
 */
export const slotDetailTargetAtom = atom<SlotDetailTarget | null>(null);

// The "open" atoms that make this panel mutually exclusive with every other
// workspace overlay live in the "Overlay coordination" section at the bottom of
// this file (`openSlotDetailAtom`, `openConfirmPanelAtom`, …), where every
// overlay atom they reference is already declared.

/**
 * Lazily fetches the deep Slot Details payload (E-12) for one slot. Triggered
 * by the panel when it opens (not reactive on mount), mirroring
 * `reconcilePreviewAtom` — a `mutationAtomWithLoadable` over a GET so the fetch
 * fires once per open with a fresh `AbortController` rather than re-running on
 * dependency changes. Read-only on the BE (no slot-list / workspace
 * invalidation needed).
 */
export const [slotDetailAtom, slotDetailLoadableAtom] =
  mutationAtomWithLoadable<
    SlotDetailResponse,
    [workspaceId: number, workspaceSlotId: number]
  >(async (_get, _set, workspaceId, workspaceSlotId) => {
    const url = `/interest_certification/workspaces/${workspaceId}/slots/${workspaceSlotId}`;
    const response = await request.get<SlotDetailResponse>(
      url,
      new AbortController()
    );
    if ('error' in response) {
      throw new Error(response.error || 'Failed to load slot details');
    }
    return response;
  });

/** Open-state for the Tasks side panel. Header writes `true`; panel writes `false`. */
export const tasksPanelOpenAtom = atom<boolean>(false);

/**
 * Comparison-table row selection. Hoisted to an atom so the Confirm Interest
 * panel's close handlers can clear it directly — keeps the cause (panel closes)
 * and effect (selection clears) co-located rather than reverse-engineering the
 * transition from the consuming table.
 */
export const comparisonTableSelectionAtom = atom<DataTableSelectionState>({
  selectAll: false,
  toggledNodes: [],
});

// ───────────────────────────────────────────────────────────────────────────
// Cross-workspace promotion sync + reconcile pull-in (INS-9740)
// ───────────────────────────────────────────────────────────────────────────

/** Payload broadcast on the entity Pusher channel when a workspace is promoted. */
export interface PromotionEvent {
  entityId: number;
  promotedWorkspaceId: number;
  promotedAt: string;
  promotedById: number | null;
  promotedByName: string | null;
}

/**
 * Set when ANOTHER workspace for this entity is promoted (delivered via the
 * entity Pusher channel). Drives the non-intrusive "certified interests
 * updated" banner; cleared when the user pulls in or dismisses. Never triggers
 * an automatic refetch — the user opts in.
 */
export const promotionPendingAtom = atom<PromotionEvent | null>(null);

/** Open-state for the reconcile (pull-in) modal. */
export const reconcileModalOpenAtom = atom<boolean>(false);

/** One side of a reconcile diff: the effective confirmation, or unconfirmed. */
export interface ReconcileValue {
  confirmed: boolean;
  source?: ConfirmationSource;
  confirmedNri?: number;
  confirmedWi?: number;
}

/** A slot that differs from master. `action` is what "take master" would do. */
export interface ReconcileEntry {
  slotId: string;
  stream: StreamCode;
  /** Per-tract grain (partial confirmations): the tract this entry covers;
   *  null = the slot-level pathway. */
  subtractId: string | null;
  /** Per-UAT grain (ONCALL-668): the unit-allocation-tract this entry covers;
   *  null = the subtract-grain / slot-level pathway. */
  unitAllocationTractId: number | null;
  action: 'take_master' | 'unconfirm';
  workspaceValue: ReconcileValue;
  masterValue: ReconcileValue | null;
}

/** A master slot absent from this workspace (inventory drift); not applied. */
export interface ReconcileInformational {
  slotId: string;
  stream: StreamCode;
  subtractId: string | null;
  reason: string;
  masterValue: ReconcileValue;
}

/** Preview of the 3-way merge from `GET .../reconciliations`. */
export interface ReconcilePreview {
  autoAppliable: ReconcileEntry[];
  conflicts: ReconcileEntry[];
  informational: ReconcileInformational[];
}

/** Per-(slot,stream,tract) conflict resolution sent to `POST .../reconciliations`. */
export interface ReconcileResolution {
  slot_id: string;
  stream: StreamCode;
  subtract_id: string | null;
  unit_allocation_tract_id: number | null;
  choice: 'master' | 'keep';
}

/**
 * Fetches the reconcile preview (no writes). Read-only; allowed on a promoted
 * workspace too. GET returns the data object directly or `{ error }`.
 */
export const [reconcilePreviewAtom, reconcilePreviewLoadableAtom] =
  mutationAtomWithLoadable<ReconcilePreview, [workspaceId: number]>(
    async (_get, _set, workspaceId) => {
      const url = `/interest_certification/workspaces/${workspaceId}/reconciliations`;
      const response = await request.get<ReconcilePreview>(
        url,
        new AbortController()
      );
      if ('error' in response) {
        throw new Error(response.error || 'Failed to load reconciliation');
      }
      return response;
    }
  );

/**
 * Applies the reconcile: auto_appliable take-master/unconfirm + the supplied
 * per-conflict resolutions. Refetches the slot list + workspace on success so
 * the table and counters repaint, then returns the post-apply preview.
 */
export const [reconcileApplyAtom, reconcileApplyLoadableAtom] =
  mutationAtomWithLoadable<
    ReconcilePreview,
    [workspaceId: number, resolutions: ReconcileResolution[]]
  >(async (_get, set, workspaceId, resolutions) => {
    const url = `/interest_certification/workspaces/${workspaceId}/reconciliations`;
    const response = await request.post<ReconcilePreview, { error: string }>(
      url,
      { resolutions },
      new AbortController()
    );

    if (response.type !== 'success' || !response.data) {
      const errorMessage =
        response.type === 'error' && response.data?.error
          ? response.data.error
          : 'Failed to apply reconciliation';
      throw new Error(errorMessage);
    }

    invalidateAfterConfirmationEvent(set, workspaceId);
    return response.data;
  });

// ───────────────────────────────────────────────────────────────────────────
// Overlay coordination — mutual exclusivity (INS-9897)
// ───────────────────────────────────────────────────────────────────────────

/**
 * Closes every mutually-exclusive workspace overlay so opening one never leaves
 * another stacked behind it: the Slot Details / Certify Interest side panels,
 * the Custom-loss warning, the Tasks side panel, the Promote / Reconcile /
 * Unconfirm modals, and the shared Activity drawer.
 *
 * Every "open" path below routes through this first, then sets its own atom.
 * Pass `'activity'` to keep the Activity drawer open while closing the rest —
 * the drawer is opened by the shared `ActivityFeedPanelOpenButton`, which this
 * page can't intercept, so its "opening closes the others" half is wired in
 * `page.tsx` by calling this atom with `'activity'` when the drawer opens.
 *
 * Centralizing the cross-atom coupling here keeps the rule in one place rather
 * than as imperative `set(null)` calls scattered across the open call sites.
 */
export const closeWorkspaceOverlaysAtom = atom(
  null,
  (_get, set, keepOpen?: 'activity') => {
    set(confirmPanelStateAtom, null);
    set(customLossWarningAtom, null);
    set(slotDetailTargetAtom, null);
    set(promoteModalOpenAtom, false);
    set(reconcileModalOpenAtom, false);
    set(unconfirmTargetRowAtom, null);
    set(tasksPanelOpenAtom, false);
    if (keepOpen !== 'activity') set(isActivityFeedPanelOpenAtom, false);
  }
);

/** Opens the Certify Interest panel, closing every other overlay first. */
export const openConfirmPanelAtom = atom(
  null,
  (_get, set, state: ConfirmPanelState) => {
    set(closeWorkspaceOverlaysAtom);
    set(confirmPanelStateAtom, state);
  }
);

/** Opens (or, with `null`, just closes) the Slot Details panel; clears the rest. */
export const openSlotDetailAtom = atom(
  null,
  (_get, set, target: SlotDetailTarget | null) => {
    set(closeWorkspaceOverlaysAtom);
    set(slotDetailTargetAtom, target);
  }
);

/** Opens the Promote modal, closing every other overlay first. */
export const openPromoteModalAtom = atom(null, (_get, set) => {
  set(closeWorkspaceOverlaysAtom);
  set(promoteModalOpenAtom, true);
});

/** Opens the Reconcile (pull-in) modal, closing every other overlay first. */
export const openReconcileModalAtom = atom(null, (_get, set) => {
  set(closeWorkspaceOverlaysAtom);
  set(reconcileModalOpenAtom, true);
});

/** Opens the Unconfirm modal for `row`, closing every other overlay first. */
export const openUnconfirmModalAtom = atom(
  null,
  (_get, set, row: SlotInterestRow) => {
    set(closeWorkspaceOverlaysAtom);
    set(unconfirmTargetRowAtom, row);
  }
);

/**
 * Toggles the Tasks side panel. Opening it closes every other overlay first;
 * closing it just clears the panel (preserving the original toggle behaviour).
 */
export const toggleTasksPanelAtom = atom(null, (get, set) => {
  if (get(tasksPanelOpenAtom)) {
    set(tasksPanelOpenAtom, false);
  } else {
    set(closeWorkspaceOverlaysAtom);
    set(tasksPanelOpenAtom, true);
  }
});
